import React, { useState, useEffect } from 'react';
import { X, Receipt, Download, Sparkles, ExternalLink, CheckCircle, Clock, AlertCircle, Calendar, Euro } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const InvoicesModal = ({ isOpen, onClose, customerId, customerName }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && customerId) {
      fetchInvoices();
    }
  }, [isOpen, customerId]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stripe/invoices/${customerId}`, { withCredentials: true });
      setInvoices(response.data);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      toast.error('Erreur lors du chargement des factures');
    } finally {
      setLoading(false);
    }
  };

  const downloadCustomPdf = async (invoiceId, invoiceNumber) => {
    try {
      toast.loading('Génération du PDF...', { id: 'pdf-download' });
      
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        responseType: 'blob',
        withCredentials: true
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Facture_${invoiceNumber || invoiceId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF téléchargé !', { id: 'pdf-download' });
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Erreur lors du téléchargement', { id: 'pdf-download' });
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      paid: { icon: CheckCircle, text: 'Payée', color: 'bg-green-500/20 text-green-400' },
      open: { icon: Clock, text: 'En attente', color: 'bg-yellow-500/20 text-yellow-400' },
      draft: { icon: Receipt, text: 'Brouillon', color: 'bg-slate-500/20 text-slate-400' },
      uncollectible: { icon: AlertCircle, text: 'Impayée', color: 'bg-red-500/20 text-red-400' },
      void: { icon: X, text: 'Annulée', color: 'bg-slate-500/20 text-slate-400' }
    };
    const badge = badges[status] || badges.draft;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl mx-4 bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#DFFF00]/20 flex items-center justify-center">
              <Receipt className="w-5 h-5 text-[#DFFF00]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Mes factures</h2>
              <p className="text-slate-400 text-sm">{customerName}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-slate-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-[#DFFF00] border-t-transparent rounded-full"></div>
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Aucune facture</h3>
              <p className="text-slate-400">Vos factures apparaîtront ici après vos premiers paiements</p>
            </div>
          ) : (
            <div className="space-y-3">
              {invoices.map((invoice) => (
                <div 
                  key={invoice.id}
                  className="bg-slate-800 rounded-xl p-4 border border-slate-700 hover:border-slate-600 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-white font-mono text-sm">{invoice.number || '-'}</span>
                        {getStatusBadge(invoice.status)}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {invoice.created_date}
                        </span>
                        {invoice.period_start && invoice.period_end && (
                          <span className="text-slate-500">
                            {invoice.period_start} - {invoice.period_end}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-lg font-bold text-white">{formatAmount(invoice.amount_due)}</div>
                        {invoice.status === 'paid' && (
                          <div className="text-xs text-green-400">Payé</div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {/* Custom PDF Download - Primary action */}
                        <Button
                          size="sm"
                          onClick={() => downloadCustomPdf(invoice.id, invoice.number)}
                          className="bg-[#DFFF00] hover:bg-[#c8e600] text-black"
                          title="Télécharger PDF"
                        >
                          <Sparkles className="w-4 h-4 mr-1" />
                          <Download className="w-4 h-4" />
                        </Button>
                        
                        {/* View hosted invoice */}
                        {invoice.hosted_invoice_url && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(invoice.hosted_invoice_url, '_blank')}
                            className="border-slate-600 text-slate-300 hover:bg-slate-700"
                            title="Voir en ligne"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 bg-slate-800/50">
          <p className="text-xs text-slate-500 text-center">
            Les factures PDF sont générées avec le branding Phocea Rent
          </p>
        </div>
      </div>
    </div>
  );
};

export default InvoicesModal;
